import { useEffect, useState } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Close } from 'blaze-ui/icons'
import { Check02 } from 'blaze-ui/icons/16'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { VisualStylesModal } from 'components/VisualStylesModal'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { GenerationLoading } from 'screens/VirtualMarketerOnboarding/BrandVisualStyleSelection/GenerationLoading'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import { StyledImageData } from '../VisualStylesForExistingUsersModal'
import Styles from '../VisualStylesForExistingUsersModal.module.scss'

interface VisualStyleSelectionStepProps {
  selectedStyleId: number | null
  onSelectStyle: (styleId: number) => void
  onNext: () => void
  onBack: () => void
  onClose: () => void
  sourceImage: MediaFile | null
  styledImages: StyledImageData[]
  onBrowseStyleSelect: (styleId: number) => void
}

export const VisualStyleSelectionStep = ({
  selectedStyleId,
  onSelectStyle,
  onNext,
  onBack,
  onClose,
  sourceImage,
  styledImages,
  onBrowseStyleSelect,
}: VisualStyleSelectionStepProps) => {
  const { openModal } = useModals()
  const [didManuallyChangeVisualStyle, setDidManuallyChangeVisualStyle] = useState(false)

  // Auto-select first style when available
  useEffect(() => {
    if (selectedStyleId === null && styledImages.length > 0) {
      onSelectStyle(styledImages[0].id)
    }
  }, [selectedStyleId, styledImages, onSelectStyle])

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-visual-style-step',
    })
  }, [])

  const handleOpenVisualStylesModal = () => {
    openModal(
      VisualStylesModal,
      {
        selectedStyleId,
        onConfirm: (styleId: number) => {
          setDidManuallyChangeVisualStyle(true)
          onBrowseStyleSelect(styleId)
        },
      },
      { newStack: true },
    )
  }

  const handleNext = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-visual-style-step',
      visual_style_id: selectedStyleId ?? undefined,
      did_manually_change_visual_style: didManuallyChangeVisualStyle,
    })
    onNext()
  }

  const isAnyLoading = styledImages.some((s) => s.isLoading)

  const footer = (
    <StickyFooter>
      <FooterButtons>
        <div className={Styles.footerSide}>
          <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
            Back
          </Button>
        </div>
        <div className={Styles.footerCenter}>
          <Button variant="secondary" size="lg" onPress={handleOpenVisualStylesModal} isDisabled={isAnyLoading}>
            Browse all styles
          </Button>
        </div>
        <div className={Styles.footerSide}>
          <Button
            variant="primary"
            size="lg"
            onPress={handleNext}
            isDisabled={!selectedStyleId || isAnyLoading}
            aria-label="Looks Good"
          >
            Looks Good
          </Button>
        </div>
      </FooterButtons>
    </StickyFooter>
  )

  return (
    <Modal.Content className={Styles.visualStyleContent}>
      <Button size="sm" variant="tertiary" onPress={onClose} className={Styles.stepCloseButton}>
        <Close />
      </Button>
      <StickyFooterProvider footer={footer}>
        <div className={Styles.visualStyleHeader}>
          <Heading level={1}>Select a photography style for your brand</Heading>
          <Text color="var(--dark-40)" variant="primary">
            We'll apply this style to all generated images to keep your content visually consistent
          </Text>
        </div>

        <div className={Styles.styledPreviewGrid}>
          {styledImages.map((style) => (
            <div className={Styles.styledPreviewCard} key={style.id} onClick={() => onSelectStyle(style.id)}>
              {selectedStyleId === style.id && (
                <div className={Styles.selectedCheck}>
                  <Check02 size={15} color="var(--dark-80)" />
                </div>
              )}

              {!style.isLoading && style.styledUrl && (
                <img src={style.styledUrl} alt={style.name} onContextMenu={(e) => e?.preventDefault()} />
              )}

              {style.isLoading && sourceImage && (
                <div className={Styles.loadingContainer}>
                  <GenerationLoading
                    sourceImage={sourceImage}
                    stylesGeneration={{ styledUrl: style.styledUrl, isLoading: style.isLoading, hasFailed: false }}
                    onTransformedImageLoaded={() => {}}
                    isLoading={style.isLoading}
                    withTag={false}
                  />
                </div>
              )}

              <div className={Styles.styleNameOverlay}>
                <div className={Styles.styleName}>
                  <Heading color="var(--light-100)" level={3}>
                    {style.name}
                  </Heading>
                </div>
              </div>

              {style.isRecommended && <div className={Styles.recommendedTag}>Recommended</div>}
            </div>
          ))}
        </div>
      </StickyFooterProvider>
    </Modal.Content>
  )
}
